import {
    ChangeDetectionStrategy,
    Component,
    Input,
    Output,
    EventEmitter,
    inject,
    OnInit,
    OnDestroy,
} from "@angular/core";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { RouterService } from "../../_services/router.service";
import { ActivatedRoute, Data, NavigationEnd, Router } from "@angular/router";
import { filter, map, mergeMap, Subject, takeUntil } from "rxjs";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-shared-navbar",
    standalone: true,
    imports: [FaIconComponent],
    templateUrl: "./navbar.component.html",
    styleUrl: "./navbar.component.scss",
})
export class SharedNavbarComponent implements OnInit, OnDestroy {
    private readonly routerService = inject(RouterService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly ngUnsubscribe = new Subject<void>();

    @Input() showMenu = false;
    /** Apply gradient background (used by admin) */
    @Input() gradient = false;
    /** Hide hamburger button on lg+ screens (used by app with always-visible sidebar) */
    @Input() hideHamburgerOnLg = false;
    /** Hide title on xs screens (used by app) */
    @Input() hideTitleOnXs = false;

    @Output() showMenuChange = new EventEmitter<boolean>();
    /** Emitted just before navigating back — allows callers to do cleanup */
    @Output() beforeBack = new EventEmitter<void>();

    title = "";

    ngOnInit(): void {
        // Read title from current route immediately (handles initial page load)
        this.title = this.getLeafRoute(this.activatedRoute).snapshot.data["title"] ?? "";

        // Then keep updating on subsequent navigations
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.getLeafRoute(this.activatedRoute)),
                filter((route: ActivatedRoute) => route.outlet === "primary"),
                mergeMap((route: ActivatedRoute) => route.data),
                takeUntil(this.ngUnsubscribe),
            )
            .subscribe((event: Data) => {
                this.title = event["title"];
            });
    }

    private getLeafRoute(route: ActivatedRoute): ActivatedRoute {
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    hamburgerToggle(): void {
        this.showMenu = !this.showMenu;
        this.showMenuChange.emit(this.showMenu);
    }

    back(): void {
        this.beforeBack.emit();
        this.routerService.navigateBack();
    }
}
